import { useState, useCallback } from 'react';
import { ViewsAPI } from '../../../utils/api';

/**
 * useViewActions Hook
 * 
 * Handles actions for multi-campaign views including:
 * - Renaming views
 * - Changing view icons
 * - Adding/removing campaigns from views
 * 
 * @param {Object} activeView - Currently active saved view
 * @param {Function} updateActiveView - Function to update the active view
 * @param {Object} multiCampaignData - Loaded multi-campaign data
 * @returns {Object} View action handlers and state
 */
export function useViewActions({ activeView, updateActiveView, multiCampaignData }) {
  // Track which campaign is being removed (for loading state)
  const [removingCampaignId, setRemovingCampaignId] = useState(null);

  // Rename view handler
  const handleRenameView = useCallback(async (newName) => {
    if (!activeView) return;
    updateActiveView({ name: newName });
    try {
      await ViewsAPI.update(activeView.id, { ...activeView, name: newName });
    } catch (err) {
      console.error('[App] Failed to rename view:', err);
      updateActiveView({ name: activeView.name });
    }
  }, [activeView, updateActiveView]);

  // Change view icon handler
  const handleViewIconChange = useCallback(async (newIcon) => {
    if (!activeView) return;
    updateActiveView({ icon: newIcon });
    try {
      await ViewsAPI.update(activeView.id, { ...activeView, icon: newIcon });
    } catch (err) {
      console.error('[App] Failed to update view icon:', err);
      updateActiveView({ icon: activeView.icon });
    }
  }, [activeView, updateActiveView]);

  // Remove campaign from view handler
  const handleRemoveCampaignFromView = useCallback(async (campaignId) => {
    if (!activeView || !activeView.query) return;
    
    // Set loading state
    setRemovingCampaignId(campaignId);
    
    const currentScope = activeView.query.scope;
    const currentCampaignIds = multiCampaignData?.campaigns?.map(c => c.id) || [];
    
    // Build the new scope by removing the campaign
    let newScope;
    if (currentScope === 'all') {
      // Convert 'all' to explicit list minus the removed campaign
      newScope = currentCampaignIds.filter(id => id !== campaignId);
    } else if (Array.isArray(currentScope)) {
      // Filter from the current scope
      newScope = currentScope.filter(id => id !== campaignId);
      
      // If the campaign wasn't in the scope but is in loaded data, 
      // the scope might be stale - use loaded campaigns instead
      if (newScope.length === currentScope.length && currentCampaignIds.includes(campaignId)) {
        newScope = currentCampaignIds.filter(id => id !== campaignId);
      }
    } else {
      setRemovingCampaignId(null);
      return; // Invalid scope
    }
    
    // Allow empty scope - will show empty state message
    
    const updatedView = {
      ...activeView,
      query: {
        ...activeView.query,
        scope: newScope,
      },
    };
    
    // Optimistic update - pass the full updated view
    updateActiveView(updatedView);
    
    try {
      await ViewsAPI.update(activeView.id, updatedView);
    } catch (err) {
      console.error('[App] Failed to remove campaign from view:', err);
      // Revert on error
      updateActiveView(activeView);
    } finally {
      setRemovingCampaignId(null);
    }
  }, [activeView, updateActiveView, multiCampaignData]);

  // Add campaign to view handler (drag from sidebar)
  const handleAddCampaignToView = useCallback(async (campaignId) => {
    if (!activeView || !activeView.query) return;
    
    const currentScope = activeView.query.scope;
    const currentCampaignIds = multiCampaignData?.campaigns?.map(c => c.id) || [];
    
    // Check if already in view
    if (currentCampaignIds.includes(campaignId)) return;
    
    // Build new scope
    let newScope;
    if (currentScope === 'all') {
      // 'all' already includes everything, but if we're adding explicitly,
      // convert to explicit list
      newScope = [...currentCampaignIds, campaignId];
    } else if (Array.isArray(currentScope)) {
      newScope = [...currentScope, campaignId];
    } else {
      newScope = [campaignId];
    }
    
    const updatedView = {
      ...activeView,
      query: {
        ...activeView.query,
        scope: newScope,
      },
    };
    
    // Optimistic update
    updateActiveView(updatedView);
    
    try {
      await ViewsAPI.update(activeView.id, updatedView);
    } catch (err) {
      console.error('[App] Failed to add campaign to view:', err);
      // Revert on error
      updateActiveView(activeView);
    }
  }, [activeView, updateActiveView, multiCampaignData]);

  return {
    removingCampaignId,
    handleRenameView,
    handleViewIconChange,
    handleRemoveCampaignFromView,
    handleAddCampaignToView,
  };
}
